import { BRAND_KIT_TITLE } from './ProductPageLayout'

/**
 * The Product page's own words for its content sections.
 *
 * Here rather than in the cards because the cards are generic - an asset list, a text list, a
 * campaign list - and the subject each one is standing for is the page's to state. The empty
 * labels in particular can only be written here: only the page knows whether its own rail
 * narrowed a section away, which is the difference between "nothing applies" and "nothing
 * matches your filters".
 */

/**
 * Everything the asset-list card cannot know about the brand kit: what to call it, and how to
 * name the wait and the failure. (Which role reads first is stated by `toFileTypeRoles`, which
 * is what orders them.)
 */
export const BRAND_KIT_WORDS = {
	title: BRAND_KIT_TITLE,
	loadingLabel: 'Loading brand kit…',
	errorLabel: "Could not load this brand's kit. Please retry.",
}

/**
 * What an empty brand kit means, in three readings: a filter result, a scope with nothing in it,
 * or a brand nothing is classified to.
 */
export const brandKitEmptyLabel = (scopeLabel: string | null, isFiltered: boolean): string => {
	if (isFiltered) return `No brand-kit files match these filters${scopeLabel ? ` in ${scopeLabel}` : ''}.`
	if (scopeLabel) return `No brand-kit files apply in ${scopeLabel}.`
	return 'No brand-kit files are classified to this brand yet.'
}

/**
 * The same three readings for the Texts section. Reported rather than left as a bare "none", so
 * a narrowed-away list never reads as an absent one.
 */
export const textsEmptyLabel = (scopeLabel: string | null, isFiltered: boolean): string => {
	if (isFiltered) return `No texts match these filters${scopeLabel ? ` in ${scopeLabel}` : ''}.`
	if (scopeLabel) return `No texts apply in ${scopeLabel}.`
	return 'No texts are classified to this product yet.'
}

/**
 * The same three readings for the campaigns section. The widest one names the brand as well as
 * the product, because a brand-wide campaign would have been listed here too - reporting only
 * the product would understate what was looked for.
 */
export const campaignsEmptyLabel = (scopeLabel: string | null, isFiltered: boolean): string => {
	if (isFiltered) return `No campaigns match these filters${scopeLabel ? ` in ${scopeLabel}` : ''}.`
	if (scopeLabel) return `No campaigns apply in ${scopeLabel}.`
	return 'No campaigns are classified to this product or its brand yet.'
}
